import type { Command } from '@am/sim';
import { currentAccount, onAccountChange } from './account';
import {
  commandNeedsFull,
  FULL_VERSION_PRODUCT,
  type PaywallReason,
  sellsFullVersion,
} from './full-rules';
import { useGame } from './store';

export {
  commandNeedsFull,
  FREE_TOWER_LEVEL,
  FULL_VERSION_PRODUCT,
  type PaywallReason,
  sellsFullVersion,
} from './full-rules';

/*
 * The Android app is free to play up to tower level 2; the full version, one in-app purchase,
 * opens the rest: everything that needs the tower at level 3 or higher (buildings, upgrades,
 * the slip road), the large map and hosting co-op. The iPhone and iPad app is bought on the App
 * Store and the web and Windows games are whole, so for them there is nothing to buy.
 *
 * Google Play keeps the purchase: it is looked up on every start (getPurchases), so it comes
 * back on a new phone with the same Google account; the last answer is remembered for a start
 * without a connection. Nothing of this reaches the simulation: the game only declines to send
 * the commands the free game does not include, and says what the full version brings.
 *
 * Google's reviewers cannot buy anything, so one account on the game server (the Play review
 * account, see deploy/grant-full.sh) carries the full version: signed in with it, the game is
 * whole; signed out, it is free again.
 */

const CACHE_KEY = 'am27.fullVersion';

/** What Google Play (or, without a connection, its last answer) says the player bought. */
let bought = false;

function update(): void {
  useGame.setState({ fullVersion: bought || currentAccount()?.full === true });
}

/** Whether the full version's content is closed here. */
export function locked(): boolean {
  return sellsFullVersion() && !useGame.getState().fullVersion;
}

/**
 * Whether a command may go out; if not, the full version is offered instead (and nothing is
 * sent). Everywhere but the free Android game, always.
 */
export function allowCommand(command: Command): boolean {
  if (!locked()) return true;
  const reason = commandNeedsFull(command, useGame.getState().buildings);
  if (!reason) return true;
  offerFullVersion(reason);
  return false;
}

/** Shows the full version's page (Paywall.tsx), saying what was tried. */
export function offerFullVersion(reason: PaywallReason): void {
  useGame.setState({ paywall: reason });
}

type Billing = typeof import('@capgo/native-purchases');

/** The billing plugin, loaded only in the Android app. */
async function billing(): Promise<Billing | null> {
  if (!sellsFullVersion()) return null;
  try {
    return await import('@capgo/native-purchases');
  } catch {
    return null;
  }
}

function remember(owned: boolean): void {
  bought = owned;
  update();
  try {
    localStorage.setItem(CACHE_KEY, owned ? '1' : '0');
  } catch {
    // Private storage off: asked again next start.
  }
}

/** On start: what Google Play says the player owns, and the price in their currency. */
export async function initFullVersion(): Promise<void> {
  if (!sellsFullVersion()) {
    useGame.setState({ fullVersion: true });
    return;
  }
  try {
    if (localStorage.getItem(CACHE_KEY) === '1') bought = true;
  } catch {
    // No storage: Play answers below.
  }
  update();
  onAccountChange(update);
  const lib = await billing();
  if (!lib) return;
  const { NativePurchases, PURCHASE_TYPE } = lib;
  try {
    const { purchases } = await NativePurchases.getPurchases({ productType: PURCHASE_TYPE.INAPP });
    remember(
      purchases.some(
        (p) => p.productIdentifier === FULL_VERSION_PRODUCT && p.purchaseState === '1',
      ),
    );
  } catch {
    // Offline or Play unavailable: the remembered answer stands.
  }
  try {
    const { products } = await NativePurchases.getProducts({
      productIdentifiers: [FULL_VERSION_PRODUCT],
      productType: PURCHASE_TYPE.INAPP,
    });
    const price = products[0]?.priceString;
    if (price) useGame.setState({ fullPrice: price });
  } catch {
    // The page shows the button without a price.
  }
}

/** Buys the full version through Google Play; true once it is owned. */
export async function buyFullVersion(): Promise<boolean> {
  const lib = await billing();
  if (!lib) return false;
  const { NativePurchases, PURCHASE_TYPE } = lib;
  const t = await NativePurchases.purchaseProduct({
    productIdentifier: FULL_VERSION_PRODUCT,
    productType: PURCHASE_TYPE.INAPP,
  });
  // A pending payment (cash at a shop, say) is not owned yet: Play tells us on a later start.
  const owned = t.purchaseState === undefined || t.purchaseState === '1';
  if (owned) remember(true);
  return owned;
}

/** Looks the purchase up again (a new phone, a refund undone, a payment that cleared). */
export async function restoreFullVersion(): Promise<boolean> {
  const lib = await billing();
  if (!lib) return false;
  try {
    await lib.NativePurchases.restorePurchases();
  } catch {
    // getPurchases below is what counts.
  }
  await initFullVersion();
  return useGame.getState().fullVersion;
}
